import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ArrowRight, ShieldCheck, Users2, WalletCards } from "lucide-react";

import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Payments from "@/pages/Payments";
import History from "@/pages/History";
import ApiKeys from "@/pages/ApiKeys";
import Settings from "@/pages/Settings";
import ApiDocs from "@/pages/ApiDocs";

function LoginPage() {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login, token } = useAuth();

  if (token) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(err.response?.data?.detail || "Login gagal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.95),_rgba(243,244,246,0.95)_32%,_rgba(230,233,239,0.9)_100%)] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto grid min-h-[calc(100vh-3rem)] max-w-6xl gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative flex flex-col justify-between overflow-hidden rounded-[2rem] border border-slate-900/10 bg-slate-950 px-6 py-8 text-white shadow-2xl shadow-slate-950/10 sm:px-8 lg:p-10">
          <div className="pointer-events-none absolute -left-20 -top-20 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 right-0 h-72 w-72 rounded-full bg-white/5 blur-3xl" />

          <div className="relative z-10">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-white/70">
              GoPay Gateway
            </div>
            <h1 className="max-w-xl font-heading text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
              Workspace pembayaran yang clean, tajam, dan siap dipakai banyak project.
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-6 text-white/70 sm:text-base">
              Login sekali, lalu kelola payment, riwayat, API key, dan akses user dengan alur yang lebih rapi.
            </p>
          </div>

          <div className="relative z-10 mt-10 grid gap-3 sm:grid-cols-3">
            {[
              { icon: WalletCards, title: "QRIS flow", desc: "Buat payment dalam hitungan detik" },
              { icon: Users2, title: "Multi access", desc: "Pisahkan API key per web/project" },
              { icon: ShieldCheck, title: "Secure admin", desc: "Login pusat dengan kontrol penuh" },
            ].map((item) => (
              <div key={item.title} className="rounded-3xl border border-white/10 bg-white/6 p-4 backdrop-blur-md">
                <item.icon className="h-5 w-5 text-white/80" />
                <p className="mt-3 text-sm font-semibold text-white">{item.title}</p>
                <p className="mt-1 text-xs leading-5 text-white/60">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="relative z-10 mt-6 grid gap-3 sm:grid-cols-3">
            {[
              ["3 web", "API key terpisah"],
              ["1 dashboard", "Kontrol admin terpusat"],
              ["Mobile ready", "Tetap enak di HP"],
            ].map(([value, label]) => (
              <div key={value} className="rounded-3xl border border-white/10 bg-black/20 px-4 py-3">
                <p className="text-lg font-black tracking-tight text-white">{value}</p>
                <p className="text-xs text-white/55">{label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="relative flex items-center justify-center">
          <div className="absolute inset-0 -z-10 rounded-[2rem] bg-white/30 blur-3xl" />
          <div className="w-full max-w-md rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-2xl shadow-slate-950/5 backdrop-blur sm:p-8">
            <div className="mb-8">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">Admin Login</p>
              <h2 className="mt-2 font-heading text-3xl font-black tracking-tight text-foreground sm:text-4xl">
                Masuk ke dashboard
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Kelola semua pembayaran dari satu tempat.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Username
                </span>
                <input
                  data-testid="login-username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full rounded-2xl border border-input bg-background/80 px-4 py-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20"
                  placeholder="admin"
                  autoComplete="username"
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Admin Password
                </span>
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-2xl border border-input bg-background/80 px-4 py-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20"
                  placeholder="Masukkan password"
                  autoComplete="current-password"
                />
              </label>
              {error && (
                <p data-testid="login-error" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
                  {error}
                </p>
              )}
              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={submitting || !password}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? "Loading..." : "Masuk ke dashboard"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { token, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    );
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route index element={<Dashboard />} />
                    <Route path="payments" element={<Payments />} />
                    <Route path="history" element={<History />} />
                    <Route path="api-keys" element={<ApiKeys />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="api-docs" element={<ApiDocs />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
